import { useMemo, useRef, useState } from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import {
    ToolbarSpacer,
    TSTAdjustView,
    TSTTable,
    TableToolbar,
    TSTFilterBar,
    TextTool,
    TSTPagination,
} from "@impero-com/ui";
import { useTranslation } from "react-i18next";

import {
    SimpleTestingControl,
    SimpleTestingControlListDto as TestingControlListDto,
    TestingProgramViewDto as TestingProgramDto,
} from "api/generated/model";
import { mapAsResourceWithoutAction } from "utils/dtoMapping";
import { getFilterModeConfig } from "utils/table/getFilterModeConfig";

import { DeleteTestingControlsModal } from "./DeleteTestingControlsModal";
import { useTestingControlListTable } from "./useTestingControlListTable";

interface TestingControlListProps {
    data: TestingControlListDto;
    testingProgramDetails: TestingProgramDto;
    testingProgramId: number;
}

export function TestingControlList({
    data,
    testingProgramDetails,
    testingProgramId,
}: TestingControlListProps) {
    const { t } = useTranslation();
    const [
        deleteTestingControlsModalOpened,
        setDeleteTestingControlsModalOpened,
    ] = useState(false);

    const testingControlsData = useMemo(
        () =>
            mapAsResourceWithoutAction(
                data.content.toSorted(
                    Cmp.map((control) => control.resource.title, Cmp.string())
                )
            ),
        [data]
    );

    const { table, getFilterComponent, canAddException, tableId } =
        useTestingControlListTable({
            data: testingControlsData,
            linkedResources: data.linkedResources,
            testingProgramId,
        });

    const selectedRows = table.getSelectedRowModel().rows;
    const selectedTestingControlIds = selectedRows.map(
        (row) => row.original.id
    );

    const deleteToolState =
        selectedRows.length > 0 && testingProgramDetails.content.actions.delete
            ? { type: "ENABLED" as const }
            : { type: "DISABLED" as const };

    const handleDeleteClick = () => {
        setDeleteTestingControlsModalOpened(true);
    };

    const handleDeleteModalClose = () => {
        setDeleteTestingControlsModalOpened(false);
    };

    const handleDeselectAll = () => {
        table.toggleAllPageRowsSelected(false);
    };

    const modeConfig = getFilterModeConfig(t);

    const tableRef = useRef<HTMLTableElement>(null);
    return (
        <div>
            <DeleteTestingControlsModal
                testingControlIds={selectedTestingControlIds}
                open={deleteTestingControlsModalOpened}
                onClose={handleDeleteModalClose}
                deselectAll={handleDeselectAll}
            />
            <TSTFilterBar<SimpleTestingControl>
                table={table}
                tableId={tableId}
                addLabel={t("FilterBar_AddFilter")}
                globalSearchLabel={t("FilterBar_GlobalSearchPlaceholder")}
                getFilterCountLabel={(count) =>
                    t("FilterBar_FiltersApplied", { count })
                }
                rowCountLabel={t("FilterBar_RowCount", {
                    count: table.getPrePaginationRowModel().rows.length,
                })}
                hideFiltersLabel={t("FilterBar_HideFilters")}
                clearAllLabel={t("FilterBar_ClearAll")}
                getFilterComponent={getFilterComponent}
                canAddException={canAddException}
                addExceptionLabel={t("FilterBar_AddException")}
                modeConfig={modeConfig}
            />
            <TSTTable
                tableRef={tableRef}
                table={table}
                noResultsFallback={t("NoResults")}
                noSourceRowsFallback={t("NoResults")}
                toolbar={
                    <TableToolbar>
                        <TextTool
                            state={deleteToolState}
                            onClick={handleDeleteClick}
                        >
                            {t("TestingControlList_Delete")}
                        </TextTool>
                        <ToolbarSpacer />
                        <TSTAdjustView
                            table={table}
                            columnsLabel={t("AdjustView_ColumnsHeader")}
                            reorderHintLabel={t("AdjustView_DragToReorderHint")}
                            resetLabel={t("AdjustView_Reset")}
                            pinLabel={t("AdjustView_PinColumn")}
                            unpinLabel={t("AdjustView_UnpinColumn")}
                            containerRef={tableRef}
                        >
                            {t("ControlResult_AdjustView")}
                        </TSTAdjustView>
                    </TableToolbar>
                }
            />
            <TSTPagination
                tableElement={tableRef}
                table={table}
                pageLabel={t("Pagination_Page")}
                getRowCountLabel={(shown, count) =>
                    t("Pagination_RowCount", { shown, count })
                }
                getSinglePageRowCountLabel={(count) =>
                    t("Pagination_RowCountSinglePage", { count })
                }
            />
        </div>
    );
}
